import React, {
    useCallback,
    useContext,
    useEffect,
    useMemo, useRef,
    useState
} from 'react';

import { Link, Switch, Route, withRouter } from 'react-router-dom';

import AsyncSelect from 'react-select/async';
import { encodeData, get } from './util/xhr.js';
import axios from 'axios';

import { sensorSubTypeToTitle, sensorPortRegex, getNamedRoot } from './util/topic';
import { READ } from './Om7Sense';

// Import all the pages we will render as routes
import { DashboardPage } from './DashboardPage.js';
import DetailPage from './DetailPage.jsx';
import { NotificationPage } from './NotificationPage.jsx';
import { ReportingPage } from './ReportingPage.jsx';
import { RulesPage } from './RulesPage.js';
import { SettingsPage } from './SettingsPage.jsx';
import ReportingFirmwarePage from './ReportingFirmwarePage.jsx';

import { DCPage } from './DCPage.jsx';
import { PuePage } from './PuePage.jsx';
import { EventReportTab } from './EventReportTab.js';
import { BackupReportPage } from "./ReportingComponents/BackupReport.js";
import { ReportDateProvider } from "./hooks/ReportDateContext";
import { useDSSubscription } from "./hooks/useDSSubscription";
import useSWR from "swr";
import { isEmpty } from "underscore";
import {
    useLocation,
    useMatch,
    useMatches,
    useNavigate
} from "react-router-dom-v5-compat";
import { ApiConnectionContext } from "./hooks/useApiConnectionContext";
import { TranslationContext } from "./hooks/useTranslationContext";
import { LegacyContextInjector } from "./util/LegacyContextInjector";

const dsPathsWeAlwaysSubscribeTo = [
    "sensor/+",
    "sensor/+/status/frontpage_Unit",
    "sensor/+/status/frontpage_Max",
    "sensor/+/status/frontpage_Current",
    "config/omshepherd/*",
    "user/config/*",
    "connections/*"];

const getClassForVisibleSection = (thisRouteUrl, location) => {
    if (location.pathname.indexOf(thisRouteUrl) === 0) {
        return "btn active";
    }
    return "btn ";
};

const getClassForVisibleSmallSection = (thisRouteUrl, location) => {
    if (location.pathname.indexOf(thisRouteUrl) === 0) {
        return "settings active";
    }
    return "settings ";
};

const RoutedReportingPage = withRouter(ReportingPage);
const RoutedDetailPage = withRouter(DetailPage);
const RoutedDashboardPage = withRouter(DashboardPage);
const RoutedDCPage = withRouter(DCPage);
const RoutedPuePage = withRouter(PuePage);
const RoutedNotificationPage = withRouter(NotificationPage);
const RoutedReportingFirmwarePage = withRouter(ReportingFirmwarePage);
const RoutedSettingsPage = withRouter(SettingsPage);

const customStyles = {
    control: (provided) => ({
        ...provided,
        width: 240
    }), placeholder: (provided) => ({
        ...provided,
        fontSize: "0.75em"
    })
};

const formatSearchItem = (item, t, p) => {
    if (item.topic.indexOf("group/") === 0) {
        // Group result, nothing to format
        return {
            value: "dc/locate;" + item.topic.replace("group/", ""),
            label: item.name
        };
    }

    if (item.topic.indexOf("sensor/") !== 0) {
        // Skip, we only show devices and groups
    }
    const sensorId = item.topic.split("/")[1];

    // For sensor things look up names and types
    let deviceName, name;

    const namedRoot = getNamedRoot(item.topic);
    const rootTopic = p.getPath(namedRoot);
    if (rootTopic === undefined || rootTopic === null) {
        return null;
    }
    item.type = rootTopic.type;
    item.parentName = rootTopic.name;
    const sensorMatch = sensorPortRegex.exec(item.topic);

    // Let's look up the sensor name
    let key = "devices/" + sensorId;
    if (sensorMatch) {
        key += "#" + sensorMatch[2] + "/" + sensorMatch[3];
    }
    if (item.type) {
        deviceName = t("device_types." + item.type.toLowerCase())
    } else {
        deviceName = t("general.device");
    }
    deviceName += ' "' + item.parentName + '"';
    if (!sensorMatch) {
        return {
            value: key,
            label: deviceName
        };
    }

    name = sensorSubTypeToTitle[sensorMatch[2]];
    if (name) {
        name += ' "' + item.name + '" ';
    } else {
        name = item.name;
    }

    return {
        value: key,
        label: name + " (" + deviceName + ")"
    };

}

export const MainComponent = () => {
    const [ loading, setLoading ] = useState(true);
    const [ baseApiError, setBaseApiError ] = useState();
    const [ numAlerts, setNumAlerts ] = useState(-1);
    const [ numWarnings, setNumWarnings ] = useState(-1);
    const [ numCritical, setNumCritical ] = useState(-1);
    const [ criticalNotifications, setCriticalNotifications ] = useState([]);

    const navigate = useNavigate();

    const location = useLocation();

    const { loggedInAs, p, version, gatewayName, lanAddress } = useContext(ApiConnectionContext);
    const t = useContext(TranslationContext);

    const newNotificationListener = useCallback((not) => {
        if (not.severity !== "ERROR" && not.severity !== "CRITICAL" && not.severity !== "WARN") {
            return;
        }
        if (not.severity === "CRITICAL") {
            let criticalNots = Array.isArray(criticalNotifications) ? criticalNotifications : [];
            criticalNots.push(not);
            setCriticalNotifications(criticalNots);
        }
        if (not.severity === "WARN") {
            setNumWarnings((currentWarnings) => currentWarnings + 1);
        } else {
            setNumAlerts((currentAlerts) => currentAlerts + 1);
        }
    }, [ setNumWarnings, setNumAlerts, setCriticalNotifications ]);

    const updatedNotificationListener = useCallback((not) => {
        // TODO: update read counter
        // console.log("update", not);

        // Check if we are showing critical notifications and we just moved a notification from open
        // TODO: test this functionality
        if (not.severity === 'CRITICAL' && not.state !== "OPEN") {
            if (!isEmpty(criticalNotifications)) {
                setCriticalNotifications((currentCriticalNotifications) =>
                    currentCriticalNotifications.filter((showNotification) => showNotification.id !== not.id));
            }
        }
    }, [ setCriticalNotifications ]);

    const notificationListeners = useMemo(() => ({ newNotification: newNotificationListener, updatedNotification: updatedNotificationListener }),
        [ newNotificationListener, updatedNotificationListener ]);

    useEffect(() => {
        // Collect all fetch promises
        const promises = dsPathsWeAlwaysSubscribeTo.map( (dsPath) => p.subscribePush(dsPath));
        Promise.all(promises).then(() => {
            setLoading(false);
        }).catch((error) => setBaseApiError(error));
        return () => {
            dsPathsWeAlwaysSubscribeTo.map((dsPath) => p.unsubscribePush(dsPath));
        };
    }, [  ]);

    const { data: rawNotifications, error: notificationsError, isLoading: notificationsLoading } = useSWR('/api/notification/count/severity');

    useEffect(() => {
        if (!notificationsLoading && !notificationsError) {
            let numAlerts = 0, numWarnings = 0;
            if (rawNotifications?.ERROR) {
                numAlerts += +rawNotifications?.ERROR;
            }
            if (rawNotifications.CRITICAL) {
                numAlerts += +rawNotifications?.CRITICAL;
                setNumCritical(+rawNotifications?.CRITICAL);
            }
            if (rawNotifications?.WARN) {
                setNumWarnings(+rawNotifications?.WARN);
            }
            setNumAlerts(numAlerts);
        }
    }, [ notificationsError, rawNotifications, notificationsLoading ]);

    useEffect(() => {
        if (numCritical <= 0) {
            return;
        }
        // Load unread critical notifications
        axios.get('/api/notification/get?severity[]=CRITICAL&state[]=OPEN').then((response) =>  {
            if (!response || !response.data) {
                return;
            }

            const notifications = response.data?.notifications;
            setCriticalNotifications(notifications);
        });
    }, [ numCritical ]);

    useEffect(() => {
        if (notificationsLoading || notificationsError) {
            return;
        }
        p.addNotificationListener(notificationListeners);
    }, [ notificationsLoading,  notificationsError ]);

    const handleLogout = useCallback((ev) => {
        ev.preventDefault();
        p.closeSession("manual");
    }, [ p ]);

    // Debounce searches
    const searchTimeout = useRef(0);

    const doSearch = useCallback((input,) => {
        return new Promise((resolve, reject) => {
            input = input.trim();
            if (searchTimeout.current) {
                clearTimeout(searchTimeout.current);
            }

            if (!input) {
                resolve([]);
                return;
            }

            searchTimeout.current = setTimeout(() => {
                let query = "/api/name?" + encodeData({q: input});

                axios.get(query).then(result => {
                    const groups = [], sensors = [], devices = [], energy = [];
                    const options = [];
                    const items = result.data;
                    const sensorIdGroups = {};
                    for (let item of items) {
                        const formattedItem = formatSearchItem(item, t, p);
                        // Disregard nonexistant items
                        if (!formattedItem) {
                            continue;
                        }
                        switch (item.type) {
                            case "g":   // groups
                                groups.push(formattedItem);
                                break;
                            case "n":   // device name
                            case "l":   // device location
                                devices.push(formattedItem);
                                break;
                            case "i":
                            case "o":
                                energy.push(formattedItem);
                                break;
                            default:
                                sensors.push(formattedItem);
                                break;

                        }
                    }
                    resolve([
                        {
                            label: "Groups",
                            options: groups
                        }, {
                            label: "Sensors",
                            options: sensors
                        },  {
                            label: "Devices",
                            options: devices
                        },  {
                            label: "Energy",
                            options: energy
                        }
                    ]);
                }, function(err) {
                    console.info("Cannot get search", err);
                    reject(err);
                });

            }, 100);
        });
    }, [ t, p ]);


    const openSearch = useCallback((option) => {
        if (option && option.value) {
            let route = "/" + option.value;

            if (option.value.indexOf("dc=group/") === 0) {
                // Lookup the dc row
                const dcItem = option.value.substring(9);

                const groupCtrl = p.DS.groupctrl;
                let items = [dcItem];
                let nextItem = groupCtrl[dcItem];
                let nextItemPath = dcItem;
                while (nextItem && nextItem.parent !== 0) {
                    items.push("group;" + nextItemPath);
                    nextItemPath = nextItem.parent;
                    nextItem = groupCtrl[nextItem.parent];
                }
                items.push("gtypes;DC");

                route = "/dc/" + items.reverse().join("%7C");
            }
            navigate(route);
        }
    }, [ navigate, p]);

    const anyErrors = baseApiError || notificationsError;

    let currentPduName = "";
    let detailLink = null;
    let detailUrl = "#";

    // if (p.DS.sensor) {
    //     if (self.state.currentDetailUuid) {
    //         var device = p.DS.sensor[self.state.currentDetailUuid];
    //         if (device && device.config && device.name) {
    //             detailUrl = "/detail-" + self.state.currentDetailUuid;
    //             detailLink = (
    //                 <Link to={detailUrl}>
    //                     <h4>{t('main.details')}</h4>
    //                     <h5 id="currentDetailPdu">{device.name}</h5>
    //                 </Link>
    //             );
    //         }
    //     }
    // }

    if (!detailLink) {
        detailLink = (
            <a href="#">
                <h4>{t('main.details')}</h4>
                <h5 id="currentDetailPdu">{t('general.no_device_selected')}</h5>
            </a>
        );
    }

    const alertButtonLabel = useMemo(() => {
        const alertText = t('main.alarms');
        if (numAlerts > 0) {
            return <>
                { alertText }
                <b>{ numAlerts }</b>
            </>;
        } else {
            return <>
                { alertText }
            </>;
        }
    }, [ numAlerts ]);


    const criticalNotificationToasts = useMemo(() => {
        let criticalToasts = [];
        for (let n of criticalNotifications) {
            const hideNotification = (ev) => {
                setCriticalNotifications((criticalNotifications) => criticalNotifications.filter((thisNot) => thisNot.id !== n.id));
                ev.stopPropagation();
                return false;
            };

            const goToCriticalNotifications = () => {
                navigate("/notifications?severity=critical&state=open%2Cinvestigating");
            };
            criticalToasts.push((
                <div key={ `crit_${n.id}` } className="toast toast-error" onClick={ goToCriticalNotifications }>
                    <button onClick={hideNotification} className="btn btn-clear float-right"></button>
                    { n.message }
                </div>
            ));
        }
        return criticalToasts;
    }, [ criticalNotifications ]);

    const isShowingNotificationsPage = useMatch('/notifications');
    const mainNavigation = useMemo(() => {
        const mainNav = [];
        if (p.checkPermission(READ, "group") && p.checkPermission(READ,
            "groupctrl")) {
            mainNav.push(<Link key="mainNavDC" to="/dc"
                               className={getClassForVisibleSection(
                                   "/dc", location)}>{t("main.dc")}</Link>);
        }
        if (p.checkPermission(READ, "sensor")) {
            mainNav.push(<Link key="mainNavDevices" to="/devices"
                               className={getClassForVisibleSection(
                                   "/devices", location)}>{t("main.devices")}</Link>);
        }
        if (p.checkPermission(READ, "reports")) {
            mainNav.push(<Link key="mainNavReports" to="/reporting"
                               className={getClassForVisibleSection(
                                   "/reporting", location)}>{t(
                "main.reports")}</Link>);
        }
        // For now, notifications are always visible
        mainNav.push(<Link key="mainNavNotifications"
                           to="/notifications?severity=error&state=open%2Cinvestigating"
                           className={getClassForVisibleSection(
                               "/notifications", location)}>{ alertButtonLabel }</Link>);
        return mainNav;
    }, [ p, loggedInAs, alertButtonLabel, location ]);

    const subNavigation = useMemo(() => {
        const subNav = [];
        if (p.checkPermission(READ, "thr")) {
            subNav.push(<Link key="subNavRules" to="/rules"
                              className={getClassForVisibleSmallSection(
                                  "/rules", location)}>{t("main.rules")}</Link>);
        }

        if (p.checkPermission(READ, "config")) {
            subNav.push(<Link key="subNavSettings" to="/settings"
                              className={getClassForVisibleSmallSection(
                                  "/settings", location)}>{t("main.settings")}</Link>);
        }
        return subNav;
    }, [ p, loggedInAs, location ]);

    if ((loading || notificationsLoading) && !anyErrors) {
        return (
            <div className="loader-element">
                <div className="loading loading-lg"></div>
            </div>
        );
    }

    // Check if there is a theme brand style
    const themeClass = t("style.logo_class");
    return (
        <>
            <header className="navbar">
                <section className="navbar-section">

                    <div className="btn-group">
                        { mainNavigation }
                    </div>

                </section>
                <section className="navbar-center">
                    <a href="/" className={"navbar-brand " + themeClass}>Om7</a>

                </section>
                <section className="navbar-section">
                    <AsyncSelect
                        value=""
                        styles={customStyles}
                        placeholder={t('main.search_placeholder')}
                        loadOptions={doSearch}
                        onChange={openSearch} />
                    { subNavigation }
                    <a onClick={handleLogout} className="settings" href="#">{ t("main.logout") }</a>
                    <div className="settings user">User: <b>{ loggedInAs }</b></div>
                </section>
            </header>

            <div className="main">
                <LegacyContextInjector t={t} p={p} loggedInAs={loggedInAs} version={version} gatewayName={gatewayName} lanAddress={lanAddress}>
                    { !isShowingNotificationsPage && criticalNotificationToasts }
                    <Switch>
                        <Route path="/devices/:uuid/:tab?">
                            <RoutedDetailPage t={t} p={p} baseApiError={baseApiError} />
                        </Route>
                        <Route path="/devices">
                            <RoutedDashboardPage t={t} p={p} baseApiError={baseApiError} />
                        </Route>
                        <Route path="/dc/:path?">
                            <RoutedDCPage baseApiError={baseApiError} />
                        </Route>
                        <Route path="/notifications/:severity?/:state?/:id?">
                            <RoutedNotificationPage />
                        </Route>
                        <Route path="/reporting/:cmd?/:id?">
                            <RoutedReportingPage />
                        </Route>
                        <Route path="/rules*">
                            <RulesPage />
                        </Route>
                        <Route path="/reporting-firmware">
                            <RoutedReportingFirmwarePage />
                        </Route>
                        <Route path="/reporting-pue">
                            <RoutedPuePage />
                        </Route>
                        <Route path="/reporting-events">
                            <EventReportTab />
                        </Route>
                        <Route path="/reporting-backups">
                            <ReportDateProvider>
                                <BackupReportPage title={t("reporting_backups.title")} />
                            </ReportDateProvider>
                        </Route>

                        <Route path="/settings">
                            <RoutedSettingsPage />
                        </Route>
                    </Switch>
                </LegacyContextInjector>
            </div>
        </>
    );
}
